import React from 'react';
import {
  Search,
  TrendingUp,
  Users,
  Link2,
  Sparkles,
  RefreshCw,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';

const STALE_DAYS = 7;
const LOW_CITATION_THRESHOLD = 5;

function daysSince(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.floor((Date.now() - t) / (1000 * 60 * 60 * 24));
}

/**
 * ResearchCredibility - a banner explaining the research that backed the
 * user's posts, with an optional "Refresh sources" CTA when the bundle
 * looks suspiciously thin or stale.
 *
 * All numbers come directly from the analysis doc - never hard-coded.
 */
export default function ResearchCredibility({
  postsCount = 10,
  topicsCount = 0,
  leadersCount = 0,
  sourcesCount = 0,
  role = '',
  industry = '',
  country = '',
  // refresh controls
  bundleFetchedAt = '',
  canRefresh = false,
  refreshing = false,
  postsStale = false,
  onRefresh = null,
}) {
  // If we genuinely have no signals, hide the banner - never show zeros.
  const hasSignal = topicsCount > 0 || leadersCount > 0 || sourcesCount > 0;
  if (!hasSignal) return null;

  const ageDays = daysSince(bundleFetchedAt);
  const isLowSources = sourcesCount > 0 && sourcesCount < LOW_CITATION_THRESHOLD;
  const isStale = ageDays !== null && ageDays > STALE_DAYS;
  const showRefresh = canRefresh && (isLowSources || isStale);

  // Build the contextual line ("Filtered for ...") only from non-empty pieces
  const contextBits = [role, industry, country].filter(Boolean);
  const contextLine = contextBits.length ? contextBits.join(' \u00b7 ') : '';

  // Friendly age label
  let ageLabel = '';
  if (ageDays !== null) {
    if (ageDays <= 0) ageLabel = 'today';
    else if (ageDays === 1) ageLabel = '1 day ago';
    else if (ageDays < 30) ageLabel = `${ageDays} days ago`;
    else ageLabel = `${Math.floor(ageDays / 30)} mo ago`;
  }

  const stats = [
    {
      icon: TrendingUp,
      n: topicsCount,
      label: topicsCount === 1 ? 'trending topic analyzed' : 'trending topics analyzed',
      tint: 'bg-[hsl(var(--sky))]/35',
    },
    {
      icon: Users,
      n: leadersCount,
      label: leadersCount === 1 ? 'top-voice profile analyzed*' : 'top-voice profiles analyzed*',
      tint: 'bg-[hsl(var(--mint))]/40',
    },
    {
      icon: Link2,
      n: sourcesCount,
      label: sourcesCount === 1 ? 'high-signal source cited' : 'high-signal sources cited',
      tint: 'bg-[hsl(var(--peach))]/45',
      highlight: isLowSources,
    },
  ].filter((s) => s.n > 0);

  return (
    <Card
      className="border-border overflow-hidden"
      data-testid="research-credibility"
      style={{
        background:
          'linear-gradient(135deg, hsl(var(--card)) 0%, hsl(var(--sky) / 0.18) 60%, hsl(var(--mint) / 0.18) 100%)',
      }}
    >
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <span
            className="h-9 w-9 rounded-xl bg-primary/12 inline-flex items-center justify-center shrink-0"
            aria-hidden
          >
            <Search size={16} className="text-primary" strokeWidth={2.25} />
          </span>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-[10px] font-mono-edge tracking-[0.18em] text-primary">
                  THE RESEARCH BEHIND THESE POSTS
                </p>
                <span className="inline-flex items-center gap-1 rounded-full bg-card border border-border px-2 py-0.5 text-[10px] font-medium text-foreground/75">
                  <Sparkles size={9} className="text-primary" /> Auto-grounded
                </span>
                {ageLabel && (
                  <span
                    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${
                      isStale
                        ? 'bg-[hsl(var(--warning))]/15 border-[hsl(var(--warning))]/40 text-[hsl(var(--warning))]'
                        : 'bg-card border-border text-foreground/70'
                    }`}
                    data-testid="research-credibility-age"
                    title={bundleFetchedAt}
                  >
                    Sourced {ageLabel}
                  </span>
                )}
              </div>

              {showRefresh && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={onRefresh}
                  disabled={refreshing}
                  className="h-8 rounded-full px-3 text-[12px] font-medium border-primary/40 text-primary hover:bg-primary/5"
                  data-testid="research-credibility-refresh"
                >
                  {refreshing ? (
                    <>
                      <Loader2 size={13} className="mr-1.5 animate-spin" />
                      Refreshing sources...
                    </>
                  ) : (
                    <>
                      <RefreshCw size={13} className="mr-1.5" />
                      Refresh sources
                    </>
                  )}
                </Button>
              )}
            </div>

            <p className="mt-2 text-[14px] sm:text-[14.5px] text-foreground/85 leading-snug">
              To shape your{' '}
              <span className="font-semibold text-foreground">{postsCount} voice-matched posts</span>,
              our engine analyzed:
            </p>

            {/* Stat chips */}
            <div className="mt-3 flex flex-wrap gap-2" data-testid="research-credibility-stats">
              {stats.map(({ icon: Icon, n, label, tint, highlight }) => (
                <div
                  key={label}
                  className={`inline-flex items-center gap-2 rounded-full ${tint} border ${
                    highlight ? 'border-[hsl(var(--warning))]/55 ring-1 ring-[hsl(var(--warning))]/30' : 'border-border/60'
                  } pl-2 pr-3 py-1.5`}
                  data-testid={`research-stat-${label.split(' ').slice(1).join('-')}`}
                >
                  <span className="h-6 w-6 rounded-full bg-white inline-flex items-center justify-center shrink-0">
                    <Icon size={12} className="text-primary" strokeWidth={2.25} />
                  </span>
                  <span className="text-[13px] leading-none">
                    <span className="font-bold text-foreground">{n}</span>{' '}
                    <span className="text-foreground/75">{label}</span>
                  </span>
                </div>
              ))}
            </div>

            {/* Quality / filter line */}
            {contextLine && (
              <p
                className="mt-3 text-[12px] text-muted-foreground"
                data-testid="research-credibility-context"
              >
                <span className="font-mono-edge tracking-wide">QUALITY FILTERS</span>
                <span className="mx-2 opacity-50">{'\u00b7'}</span>
                Tuned to <span className="text-foreground/85 font-medium">{contextLine}</span>
                <span className="mx-2 opacity-50">{'\u00b7'}</span>
                Sources refreshed at generation time
              </p>
            )}

            {/* Stale-research nudge (shown when refresh is recommended but not yet done) */}
            {showRefresh && !postsStale && (
              <div
                className="mt-3 flex items-start gap-2 rounded-lg border border-[hsl(var(--warning))]/35 bg-[hsl(var(--warning))]/8 px-3 py-2"
                data-testid="research-credibility-stale-hint"
              >
                <AlertTriangle
                  size={13}
                  className="text-[hsl(var(--warning))] mt-0.5 shrink-0"
                  strokeWidth={2.25}
                />
                <p className="text-[12px] text-foreground/85 leading-snug">
                  {isLowSources && isStale
                    ? `Only ${sourcesCount} citation${sourcesCount === 1 ? '' : 's'} and ${ageLabel} - we recommend refreshing sources for stronger PoV posts.`
                    : isLowSources
                      ? `Citation pool is thin (${sourcesCount} source${sourcesCount === 1 ? '' : 's'}). Refresh to pull more high-signal sources.`
                      : `Sources were fetched ${ageLabel}. Refresh to keep your posts grounded in the latest news.`}
                </p>
              </div>
            )}

            {/* Post-refresh nudge (shown when bundle is fresh but posts are stale) */}
            {postsStale && (
              <div
                className="mt-3 flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/8 px-3 py-2"
                data-testid="research-credibility-posts-stale"
              >
                <RefreshCw size={13} className="text-primary mt-0.5 shrink-0" strokeWidth={2.25} />
                <p className="text-[12px] text-foreground/85 leading-snug">
                  Sources refreshed. Your existing posts still use the previous citations -{' '}
                  <span className="font-semibold text-foreground">regenerate posts</span> to ground
                  them in the latest research.
                </p>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
